import { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  description?: string;
}

interface SearchableDropdownProps {
  value: string;
  options: SearchableDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  dropdownClassName?: string;
  menuTitle?: string;
  emptyMessage?: string;
  loading?: boolean;
  loadingMessage?: string;
  allowClear?: boolean;
  /** Extra classes for the floating menu panel (e.g. z-index). */
  menuClassName?: string;
  /** Compact 28px toolbar height (matches Workload filter controls). */
  compact?: boolean;
  /** Form-friendly 32px height aligned with settings inputs. */
  size?: 'md' | 'sm';
}

export default function SearchableDropdown({
  value,
  options,
  onChange,
  placeholder = 'Type to select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  className = '',
  dropdownClassName = 'w-[260px]',
  menuTitle = 'Select Option',
  emptyMessage = 'No matches found',
  loading = false,
  loadingMessage = 'Loading...',
  allowClear = false,
  menuClassName = '',
  compact = false,
  size = 'md',
}: SearchableDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [open]);

  const selected = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;

    return options.filter((opt) => {
      const label = opt.label.toLowerCase();
      const desc = (opt.description || '').toLowerCase();
      return label.includes(query) || desc.includes(query);
    });
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, open]);

  const commitSelection = (nextValue: string) => {
    onChange(nextValue);
    setSearch('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const displayText = search || selected?.label || '';

  const inputBaseClass = compact
    ? 'w-full h-7 min-h-[28px] max-h-7 pl-2 pr-7 py-0 text-[11px] leading-tight box-border bg-surface border border-border rounded-[var(--radius-sm)] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-focus focus:shadow-focus transition-all truncate'
    : size === 'sm'
      ? 'w-full h-8 min-h-8 pl-3 pr-8 py-1.5 text-[12.5px] leading-tight box-border bg-surface border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-focus focus:ring-2 focus:ring-brand/25 transition-all truncate'
      : 'w-full pl-3 pr-8 py-2 text-[13px] bg-surface border border-border rounded-[var(--radius-sm)] text-text-primary placeholder:text-text-muted focus:outline-none focus:border-border-focus focus:shadow-focus transition-all truncate';

  return (
    <div className="relative" data-no-row-click="true">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayText}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!disabled) setOpen(true);
          }}
          onFocus={() => {
            if (!disabled) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (disabled) return;

            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setHighlightedIndex((current) => Math.min(current + 1, Math.max(filteredOptions.length - 1, 0)));
            }

            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              setHighlightedIndex((current) => Math.max(current - 1, 0));
            }

            if (e.key === 'Enter' && open && filteredOptions[highlightedIndex]) {
              e.preventDefault();
              commitSelection(filteredOptions[highlightedIndex].value);
            }

            if (e.key === 'Escape') {
              setOpen(false);
              setSearch('');
            }
          }}
          placeholder={selected?.label || placeholder}
          disabled={disabled}
          className={`${inputBaseClass} ${className}`}
        />
        <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-1 ${compact ? 'right-1.5' : 'right-2'}`}>
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={() => commitSelection('')}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              title="Clear selection"
            >
              <FiX className="h-3.5 w-3.5" />
            </button>
          )}
          <FiChevronDown className={`text-gray-400 pointer-events-none ${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
        </div>
      </div>

      {open && !disabled && (
        <div ref={dropdownRef} className={`absolute top-full left-0 mt-1 bg-surface border border-border rounded-[var(--radius-md)] shadow-lg z-[300] p-2 ${dropdownClassName} ${menuClassName}`}>
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-xs font-semibold text-slate-600">{menuTitle}</p>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSearch('');
              }}
              className="inline-flex items-center justify-center h-6 w-6 rounded border border-slate-200 text-slate-500 hover:bg-slate-100"
              title="Close"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="relative mb-2">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightedIndex((current) => Math.min(current + 1, Math.max(filteredOptions.length - 1, 0)));
                }

                if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightedIndex((current) => Math.max(current - 1, 0));
                }

                if (e.key === 'Enter' && filteredOptions[highlightedIndex]) {
                  e.preventDefault();
                  commitSelection(filteredOptions[highlightedIndex].value);
                }

                if (e.key === 'Escape') {
                  setOpen(false);
                  setSearch('');
                  inputRef.current?.focus();
                }
              }}
              placeholder={searchPlaceholder}
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="max-h-60 overflow-y-auto overscroll-contain">
            {loading ? (
              <p className="px-2 py-2 text-sm text-gray-500">{loadingMessage}</p>
            ) : filteredOptions.length === 0 ? (
              <p className="px-2 py-2 text-sm text-gray-500">{emptyMessage}</p>
            ) : (
              filteredOptions.map((opt, index) => (
                <button
                  key={opt.value}
                  type="button"
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => commitSelection(opt.value)}
                  className={`w-full text-left px-2 py-2 rounded ${index === highlightedIndex ? 'bg-slate-100' : 'hover:bg-gray-100'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-gray-800 truncate">{opt.label}</div>
                    {opt.value === value && <FiCheck className="h-4 w-4 text-indigo-600 flex-shrink-0" />}
                  </div>
                  {opt.description && (
                    <div className="text-xs text-gray-500 truncate">{opt.description}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
